<template>
    <div>
        <b-card
            class="question mb-2"
            no-body
        >
            <template #header>
                <question-print-view-warning v-if="question && question.print_view" />
                <div class="question-header-content">
                    <CardID :id="question.id" type="Question"/>
                    <div class="d-flex align-items-center">
                        <div class="d-flex flex-grow-1 align-items-center form-inline">

                            <div class="flex-grow-1">
                                <div class="mr-3 text-capitalize">
                                    {{ formattedQuestionType(question.type) }}
                                </div>
                            </div>

                            <div class="ml-auto">
                                <template v-if="question.used_by_multiple_places">
                                    <b-dropdown
                                        v-if="can_duplicate_question"
                                        class="mr-2"
                                        variant="warning"
                                        size="sm"
                                    >
                                        <template #button-content>
                                            <strong>Linked Question</strong>
                                        </template>
                                        <b-dropdown-item
                                            v-if="can_duplicate_question"
                                            @click="convertToDuplicate"
                                            :disabled="!can_duplicate_question || disabled"
                                        >
                                            <font-awesome-icon :icon="['fad', 'unlink']" class="text-warning" fixed-width />
                                            Convert to Duplicate
                                        </b-dropdown-item>
                                    </b-dropdown>
                                    <b-badge
                                        v-else
                                        class="mr-2"
                                        variant="warning"
                                        size="sm"
                                    >
                                        <strong>Linked Question</strong>
                                    </b-badge>
                                </template>
                                <b-dropdown
                                    size="sm"
                                    variant="secondary"
                                    :disabled="se_locked || disabled"
                                    dropleft
                                >
                                    <template #button-content>
                                        <font-awesome-icon :icon="['fad', 'cog']" />
                                    </template>
                                    <b-dropdown-item
                                        @click.prevent="editQuestion(question)"
                                        :disabled="!can_update_article || !can_update_question || disabled"
                                    >
                                        <font-awesome-icon :icon="['fad', 'edit']" class="text-primary" fixed-width />
                                        Edit
                                    </b-dropdown-item>
                                    <b-dropdown-item
                                        @click.prevent="unlinkQuestion(question)"
                                        :disabled="!can_update_article || !can_unlink_question || disabled"
                                    >
                                        <font-awesome-icon :icon="['fad', 'unlink']" class="text-warning" fixed-width />
                                        Unlink
                                    </b-dropdown-item>
                                </b-dropdown>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <question-version-switcher :question="question">
                <question-content
                    :question="question"
                />
            </question-version-switcher>

            <b-card-footer
                v-if="!hide_standards"
                class="bg-white"
            >
                <h6>Question Standards</h6>

                <standards-multiselect
                    v-model="question.standards"
                    :standards="available_standards"
                    :state_grade="state_grade"
                    @addStandard="addStandard"
                    @removeStandard="removeStandard"
                />
            </b-card-footer>

            <question-modal
                v-model="show_question_modal"
                :unit="unit"
                :article="article"
                :question="question"
                :available_standards="available_standards"
                :state_grade="state_grade"
                @update="saveQuestion"
                @hide="show_question_modal = false"
            />
        </b-card>
    </div>
</template>

<style lang="scss" scoped>

</style>

<script>
import {mapState} from 'vuex'

import CardID from '@/components/CardID'

import QuestionContent from '@/components/questions/Index'
import QuestionPrintViewWarning from '@/components/questions/QuestionPrintViewWarning'
import QuestionVersionSwitcher from '@/components/questions/QuestionVersionSwitcher'
import QuestionModal from '@/components/modals/questions/QuestionModal'
import StandardsMultiselect from '@/components/standards/StandardsMultiselect'
import {formattedQuestionType} from '@/common/formattedQuestionType'
import moment from 'moment'

export default {
    data: () => ({
        show_question_modal: false,
        filtered_question_standards: [],
        saving_question: false,
    }),
    props: [
        'publication',
        'unit',
        'article',
        'question',
        'disabled',
        'available_standards',
        'state_grade',
        'hide_standards',
    ],
    components: {
        StandardsMultiselect,
        CardID,
        QuestionContent,
        QuestionModal,
        QuestionPrintViewWarning,
        QuestionVersionSwitcher
    },
    computed: {
        ...mapState({
            user: state => state.user.user,
        }),
        can_update_article() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_article')
        },
        can_update_question() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_question')
        },
        can_unlink_question() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'unlink_question')
        },
        can_duplicate_question() {
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'duplicate_question')
        },
        se_locked() {
            let se_locked

            if (
                this.publication.se_locked ||
                this.unit.se_locked
            ) {
                se_locked = true
            } else {
                se_locked = false
            }

            return se_locked
        }
    },
    methods: {
        formattedQuestionType,
        editQuestion() {
            this.show_question_modal = true
        },
        async saveQuestion(question) {
            if (this.saving_question) {
                return
            }

            this.saving_question = true
            try {
                await this.saveQuestionChanges(question)
            } finally {
                this.saving_question = false
            }
        },
        async saveQuestionChanges(question) {
            const hasNarration = this.question.narration && this.question.narration.id
            const isActivity = this.article && this.article.is_activity
            const questionContentChanged = this.extractNarrationText(this.question.content && this.question.content.question) !==
                this.extractNarrationText(question.content && question.content.question)

            const shouldOfferNarrationRemoval = isActivity &&
                hasNarration &&
                this.question.type === 'text_block' &&
                questionContentChanged

            let payload = {
                content: question.content,
                print_view: question.print_view,
                standards: question.standards,
                available_standards: this.available_standards,
            }
            if (this.publication && this.unit && this.article) {
                payload = {
                    ...payload,
                    publication_id: this.publication.id,
                    unit_id: this.unit.id,
                    article_id: this.article.id,
                }
            }
            await this.axios.post(`/api/questions/update/${question.id}`, payload)

            this.show_question_modal = false

            if (shouldOfferNarrationRemoval) {
                const result = await this.$swal({
                    html: 'This activity item text was updated. This item has an audio narration. Would you like to delete the audio?',
                    showCancelButton: true,
                    confirmButtonText: 'Delete Audio',
                    cancelButtonText: 'Keep Audio',
                    cancelButtonColor: '#0d6efd',
                    cancelButtonClass: 'text-white',
                    focusCancel: true,
                    reverseButtons: true
                })

                if (result.value) {
                    try {
                        await this.axios.post('/api/articles/delete_narration/' + this.question.narration.id)
                        this.$set(this.question, 'narration', {})
                    } catch (e) {
                        await this.$swal({
                            icon: 'error',
                            html: 'The activity item text was saved, but deleting the audio failed.'
                        })
                    }
                }
            }

            this.$emit('update')
        },
        extractNarrationText(html) {
            if (!html) {
                return ''
            }

            const documentFragment = new DOMParser().parseFromString(html, 'text/html')

            return (documentFragment.body.textContent || '')
                .replace(/\s+/g, ' ')
                .trim()
        },
        async unlinkQuestion(question) {
            const result = await this.$swal({
                html: 'Are you sure you want to unlink this question?',
                showCancelButton: true,
                confirmButtonText: 'Unlink Question',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#E67E22',
                reverseButtons: true
            })

            if (result.value) {
                await this.axios.post(
                    `/api/articles/remove_question/${this.article.article_id}`,
                    {
                        question_id: question.id,
                        publication_id: this.publication.id,
                        unit_id: this.unit.id,
                    }
                )
                this.$emit('unlinkQuestion')
            }
        },

        async addStandard(standard) {
            await this.axios.post('/api/questions/add_standard/' + this.question.id, {
                standard_id: standard.guid
            })
            this.$emit('update')
        },
        async removeStandard(standard) {
            await this.axios.post('/api/questions/remove_standard/' + this.question.id, {
                standard_id: standard.guid
            })

            this.$emit('update')

        },
        async convertToDuplicate() {
            try {
                const message = `
                    Are you sure you want to convert this linked question to a duplicate?
                    <div class="alert alert-danger mt-2 mx-1 px-1">
                        <p>This will change the Question ID.</p>
                        <strong>Student progress may be lost mid-year!</strong>
                    </div>
                `
                const result = await this.$swal({
                    html: message,
                    showCancelButton: true,
                    confirmButtonText: 'Convert',
                    cancelButtonText: 'Cancel',
                    confirmButtonColor: '#E67E22',
                    reverseButtons: true,
                    customClass: 'swal-wider',
                })
                if (!result.value) {
                    return
                }
                await this.axios.post(
                    `/api/questions/linked-duplicate/${this.question.id}`,
                    {
                        article_id: this.article.id,
                    }
                )
                this.$emit('update')
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to duplicate linked question: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            }
        },

    }
}
</script>

<style lang="scss" scoped>
.question::v-deep .card-header {
    padding: 0;
}

.question-header-content {
    padding: 1.25rem;
}
</style>
